/**
 * Vault Hooks
 *
 * React Query hooks for document and file management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../providers/SdkProvider';
import {
  VaultService,
  VaultFile,
  VaultCategory,
  VaultStorageUsage,
  UploadFileInput,
  UploadUrlResponse,
  DownloadUrlResponse,
  VaultUploadOptions,
  VaultDeleteResult,
} from '../services/vault.service';

// ═══════════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════════

export const vaultKeys = {
  all: ['vault'] as const,
  files: (params?: { category?: string }) => [...vaultKeys.all, 'files', params] as const,
  file: (id: string) => [...vaultKeys.all, 'file', id] as const,
  categories: () => [...vaultKeys.all, 'categories'] as const,
  storage: () => [...vaultKeys.all, 'storage'] as const,
};

// ═══════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * List files in vault
 */
export function useVaultFiles(params?: { category?: string; limit?: number; offset?: number }) {
  const client = useApiClient();
  const service = new VaultService(client);

  return useQuery<{ files: VaultFile[]; total: number }>({
    queryKey: vaultKeys.files(params),
    queryFn: () => service.listFiles(params),
  });
}

/**
 * Get single file details
 */
export function useVaultFile(fileId: string) {
  const client = useApiClient();
  const service = new VaultService(client);

  return useQuery<VaultFile>({
    queryKey: vaultKeys.file(fileId),
    queryFn: () => service.getFile(fileId),
    enabled: !!fileId,
  });
}

/**
 * Get file categories
 */
export function useVaultCategories() {
  const client = useApiClient();
  const service = new VaultService(client);

  return useQuery<VaultCategory[]>({
    queryKey: vaultKeys.categories(),
    queryFn: () => service.getCategories(),
    staleTime: 1000 * 60 * 60, // Categories rarely change
  });
}

/**
 * Get storage usage
 */
export function useVaultStorage() {
  const client = useApiClient();
  const service = new VaultService(client);

  return useQuery<VaultStorageUsage>({
    queryKey: vaultKeys.storage(),
    queryFn: () => service.getStorageUsage(),
  });
}

/**
 * Upload file (get presigned upload URL)
 */
export function useUploadFile() {
  const client = useApiClient();
  const service = new VaultService(client);
  const queryClient = useQueryClient();

  return useMutation<UploadUrlResponse, Error, UploadFileInput>({
    mutationFn: (input) => service.getUploadUrl(input),
    onSuccess: () => {
      // Invalidate ALL vault queries (files with any params + storage)
      queryClient.invalidateQueries({ queryKey: vaultKeys.all });
    },
  });
}

/**
 * Upload file data via API proxy (avoids S3 CORS).
 * Uploads the file through the platform API, which forwards to S3 server-side,
 * then auto-confirms. No need to call useConfirmUpload separately.
 */
export function useUploadViaProxy() {
  const client = useApiClient();
  const service = new VaultService(client);
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; fileId: string; status: string },
    Error,
    { fileId: string; data: Blob | File; contentType: string }
  >({
    mutationFn: ({ fileId, data, contentType }) =>
      service.uploadViaProxy(fileId, data, contentType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vaultKeys.all });
    },
  });
}

/**
 * Upload a file straight to storage: presign → PUT → confirm.
 *
 * The WEB path (DW5-1). Bytes go browser → S3 without transiting platform-api,
 * and `confirmUpload` — whose S3 existence check had no production caller
 * (H4) — is the step that decides whether the row becomes a real document or an
 * honest `rejected`. Mobile keeps the proxy: a native client has no browser PUT.
 */
export function useUploadDirect() {
  const client = useApiClient();
  const service = new VaultService(client);
  const queryClient = useQueryClient();

  return useMutation<
    { fileId: string; status: string; size: number },
    Error,
    { input: UploadFileInput; data: Blob | File } & VaultUploadOptions
  >({
    mutationFn: ({ input, data, ...options }) => service.uploadDirect(input, data, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vaultKeys.all });
    },
  });
}

/**
 * Confirm file upload
 */
export function useConfirmUpload() {
  const client = useApiClient();
  const service = new VaultService(client);
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; fileId: string; status: string; size: number },
    Error,
    string
  >({
    mutationFn: (fileId) => service.confirmUpload(fileId),
    onSuccess: () => {
      // Invalidate ALL vault queries (files with any params + storage)
      queryClient.invalidateQueries({ queryKey: vaultKeys.all });
    },
  });
}

/**
 * Get download URL for a file
 */
export function useDownloadFile() {
  const client = useApiClient();
  const service = new VaultService(client);

  return useMutation<DownloadUrlResponse, Error, string>({
    mutationFn: (fileId) => service.getDownloadUrl(fileId),
  });
}

/**
 * Delete file. The result's `evidenceRetention` names every company that still
 * retains the file under a decision record, until when and why (W5.8) — render
 * it to the citizen; an empty list means nothing was retained.
 */
export function useDeleteFile() {
  const client = useApiClient();
  const service = new VaultService(client);
  const queryClient = useQueryClient();

  return useMutation<VaultDeleteResult, Error, string>({
    mutationFn: (fileId) => service.deleteFile(fileId),
    onSuccess: (_data, fileId) => {
      // Remove single file from cache
      queryClient.removeQueries({ queryKey: vaultKeys.file(fileId) });
      // Invalidate ALL vault queries (files with any params + storage)
      queryClient.invalidateQueries({ queryKey: vaultKeys.all });
    },
  });
}

/**
 * Download several vault files as one ZIP (talent-scoped server-side).
 * Resolves to a Blob — the caller triggers the browser download via `saveBlob`.
 */
export function useDownloadVaultFilesZip() {
  const client = useApiClient();
  const service = new VaultService(client);

  return useMutation<Blob, Error, string[]>({
    mutationFn: (fileIds) => service.downloadFilesZip(fileIds),
  });
}

// Re-export types
export type {
  VaultFile,
  VaultCategory,
  VaultStorageUsage,
  UploadFileInput,
  UploadUrlResponse,
  DownloadUrlResponse,
};
